import { memo, useCallback, useMemo } from 'react'
import constants from 'app/constants'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import useAppData from 'app/services/hooks/useAppData'
import BG from 'components/core/BG/BG'
import Divider from 'components/core/Divider/Divider'
import Icon from 'components/core/Icon/Icon'
import Layout from 'components/core/Layout/Layout'
import ScrollRegion from 'components/core/ScrollRegion/ScrollRegion'
import useHandleNavClickLink from 'components/interface/hooks/useHandleNavClickLink'
import useRenderNavLinks from 'components/interface/hooks/useRenderNavLinks'
import useDelayedActiveState from 'lib/react/hooks/useDelayedActiveState'
import classNames from 'lib/util/classNames'
import styles from './BurgerMenu.module.scss'
import Footer from './Footer/Footer'
import Hero from './Hero/Hero'
import NavList from './NavList/NavList'

/**
 * The `BurgerMenu`
 *
 * @returns {React.ReactElement} the element
 */
function BurgerMenu () {
  const appData = useAppData()
  const { isBurgerMenuOpen: isActive, setIsBurgerMenuOpen } = useRootContext()
  const { account } = useUserContext()
  const isRender = useDelayedActiveState(isActive, 0, 0.3)
  const isVisible = useDelayedActiveState(isActive, 0.1, 0)
  const classNameOutput = useMemo(
    () =>
      classNames([
        styles.container,
        isRender && styles.render,
        isVisible && styles.visible
      ]),
    [isVisible, isRender]
  )

  // Handle link actions
  const handleNavLink = useHandleNavClickLink()

  /**
   * Handle clicking a link
   */
  const handleClickLink = useCallback((event, linkProps) => {
    setIsBurgerMenuOpen(false)
    handleNavLink(event, linkProps)
  }, [])

  const linksDataPrimary = useMemo(
    () => appData?.global?.navBurger?.linksPrimary || [],
    [appData]
  )
  const linksDataSecondary = useMemo(
    () => [...(appData?.global?.nav?.linksSecondary || [])],
    [appData]
  )
  const linksPrimary = useRenderNavLinks(
    linksDataPrimary,
    NavList.Item,
    handleClickLink
  )
  const linksSecondary = useRenderNavLinks(
    linksDataSecondary,
    NavList.Item,
    handleClickLink
  )

  /**
   * The disconnect wallet/sign out button
   */
  const buttonSignOut = useMemo(() => {
    if (!account.hasWallet) {
      return null
    }

    const label = account.isSignedIn ? 'Sign Out' : 'Disconnect Wallet'

    return (
      <NavList.Item
        isActive={true}
        onClick={e => {
          handleClickLink(e, { action: 'sign_out' })
        }}
      >
        <Icon icon="sign_out" />
        {label}
      </NavList.Item>
    )
  }, [account, handleClickLink])

  /**
   * Handle closing the burger menu
   */
  const handleClose = useCallback(() => {
    setIsBurgerMenuOpen(false)
  }, [setIsBurgerMenuOpen])

  return (
    <div className={classNameOutput}>
      {isRender && (
        <button className={styles.background} onClick={handleClose} />
      )}

      <div className={styles.display}>
        <Hero isActive={isVisible} />
        <div className={styles.body}>
          <BG color="e" opacity="a" mask="to-bottom" />
          <Divider mask="none" />
          <div className={styles.content}>
            <ScrollRegion className={styles.scroll}>
              {constants.MODE !== 0 && (
                <Layout.Group>
                  <Layout.Title size="h5">Navigation</Layout.Title>
                  <NavList>{linksPrimary}</NavList>
                </Layout.Group>
              )}
              <Layout.Group>
                <Layout.Title size="h5">Useful links</Layout.Title>
                <NavList type="b">
                  {linksSecondary}
                  {buttonSignOut}
                </NavList>
              </Layout.Group>
            </ScrollRegion>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  )
}

/** @type {object} */
BurgerMenu.propTypes = {}

/** @type {object} */
BurgerMenu.defaultProps = {}

// Memoize
export default memo(BurgerMenu)
